/**
 * QAOptionsPanel — Preset selection, check toggles, strictness, and context fields
 * shown BEFORE a QA run starts.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Switch, FormControlLabel,
  TextField, Select, MenuItem, Collapse, IconButton, RadioGroup, Radio,
  FormControl, InputLabel, Autocomplete, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  PRESETS, PRESET_ORDER, CATEGORY_META, STRICTNESS_OPTIONS,
  PROJECT_TYPE_OPTIONS, PLATFORM_OPTIONS, TONE_OPTIONS,
} from './qaConstants';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const STORAGE_KEY = 'videoQaOptions';

function loadSavedOptions() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return null;
}

function saveOptions(options) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch { /* ignore */ }
}

export default function QAOptionsPanel({ onStart, videoData, projectData }) {
  const saved = loadSavedOptions();

  const [selectedPreset, setSelectedPreset] = useState(saved?.preset || 'rough_cut');
  const [checks, setChecks] = useState(
    saved?.checks || { ...PRESETS.rough_cut.checks }
  );
  const [strictness, setStrictness] = useState(saved?.strictness || 'normal');
  const [context, setContext] = useState(saved?.context || {
    videoType: '', doelgroep: '', doel: '', platform: '', tone: '', cta: '', briefing: '', focus: '',
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Project search state
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectInputValue, setProjectInputValue] = useState('');
  const projectSearchTimer = useRef(null);

  // Search projects via backend API (debounced)
  const searchProjects = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProjectOptions([]);
      return;
    }
    setProjectLoading(true);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/projects?search=${encodeURIComponent(query)}`);
      if (resp.ok) {
        const data = await resp.json();
        const opts = (data.projects || []).map((p) => {
          const f = p.fields || {};
          const num = f.Projectnummer || '';
          const client = f.KlantnaamString || '';
          const name = f.Projectnaam || '';
          const label = [num, client, name].filter(Boolean).join(' - ');
          return { projectNumber: String(num), projectName: label, label };
        });
        setProjectOptions(opts);
      }
    } catch (e) {
      console.error('[QAOptionsPanel] Project search error:', e);
    } finally {
      setProjectLoading(false);
    }
  }, []);

  // Debounced project input handler
  const handleProjectInputChange = useCallback((_, value) => {
    setProjectInputValue(value);
    if (projectSearchTimer.current) clearTimeout(projectSearchTimer.current);
    projectSearchTimer.current = setTimeout(() => searchProjects(value), 300);
  }, [searchProjects]);

  // Pre-fill project from videoData.project_number (or parse from filename)
  useEffect(() => {
    if (selectedProject || prefilled) return;
    let pn = videoData?.project_number;
    // Fallback: parse from filename (e.g. "9296.ICTRecht - ...")
    if (!pn && videoData?.filename) {
      const m = videoData.filename.match(/^(\d{4,5})\b/);
      if (m) pn = m[1];
    }
    if (pn) {
      // Search for this project number to pre-fill the selector
      searchProjects(String(pn)).then(() => {
        // After search completes, try to auto-select matching project
        // (handled via separate effect below)
      });
    }
  }, [videoData, selectedProject, prefilled, searchProjects]);

  // Auto-select project when options load and we have a project_number to match
  useEffect(() => {
    if (selectedProject) return;
    let pn = videoData?.project_number;
    // Fallback: parse from filename (same logic as pre-fill effect)
    if (!pn && videoData?.filename) {
      const m = videoData.filename.match(/^(\d{4,5})\b/);
      if (m) pn = m[1];
    }
    if (pn && projectOptions.length > 0) {
      const match = projectOptions.find((o) => o.projectNumber === String(pn));
      if (match) {
        setSelectedProject(match);
        setContext((prev) => ({ ...prev, projectNumber: match.projectNumber, projectName: match.projectName }));
      }
    }
  }, [projectOptions, videoData, selectedProject]);

  // Pre-fill context from video + project data (only once)
  useEffect(() => {
    if (prefilled) return;
    const ctx = { ...context };
    let changed = false;

    // From project record — use Airtable Projecttype directly
    if (projectData) {
      if (!ctx.videoType && projectData.project_type) {
        ctx.videoType = projectData.project_type; // Direct Airtable value (Video, Interview, Explainer, etc.)
        changed = true;
      }
      if (!ctx.klant && projectData.klant_name) {
        ctx.klant = projectData.klant_name;
        changed = true;
      }
      if (!ctx.briefing && projectData.description) {
        ctx.briefing = projectData.description;
        changed = true;
      }
    }

    // Fallback: from video record category
    if (!ctx.videoType && videoData?.category) {
      ctx.videoType = videoData.category; // Frame.io category
      changed = true;
    }

    if (changed) {
      setContext(ctx);
      setPrefilled(true);
      // Auto-open context panel when we have pre-filled data
      if (ctx.briefing || ctx.videoType) setContextOpen(true);
    }
  }, [videoData, projectData, prefilled, context]);

  // When preset changes, update checks
  const handlePresetSelect = (presetKey) => {
    setSelectedPreset(presetKey);
    if (PRESETS[presetKey]) {
      setChecks({ ...PRESETS[presetKey].checks });
    }
  };

  // When individual check toggled, switch to custom
  const handleCheckToggle = (catKey) => {
    const newChecks = { ...checks, [catKey]: !checks[catKey] };
    setChecks(newChecks);

    // Check if current checks still match any preset
    const matchingPreset = PRESET_ORDER.find((pk) => {
      const presetChecks = PRESETS[pk].checks;
      return Object.keys(presetChecks).every((k) => presetChecks[k] === newChecks[k]);
    });
    setSelectedPreset(matchingPreset || 'custom');
  };

  const handleContextChange = (field, value) => {
    setContext((prev) => ({ ...prev, [field]: value }));
  };

  const handleStart = () => {
    const options = {
      preset: selectedPreset !== 'custom' ? selectedPreset : null,
      strictness,
      checks,
      context: Object.fromEntries(
        Object.entries(context).filter(([, v]) => v && v.trim())
      ),
    };
    saveOptions({ preset: selectedPreset, checks, strictness, context });
    onStart(options);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 3 }}>
      {/* Project selector — top of form for quick linkage */}
      <Autocomplete
        size="small"
        fullWidth
        options={projectOptions}
        getOptionLabel={(opt) => opt.label || ''}
        isOptionEqualToValue={(opt, val) => opt.projectNumber === val.projectNumber}
        value={selectedProject}
        onChange={(_, newVal) => {
          setSelectedProject(newVal);
          if (newVal) {
            handleContextChange('projectNumber', newVal.projectNumber);
            handleContextChange('projectName', newVal.projectName);
          } else {
            handleContextChange('projectNumber', '');
            handleContextChange('projectName', '');
          }
        }}
        inputValue={projectInputValue}
        onInputChange={handleProjectInputChange}
        loading={projectLoading}
        noOptionsText={projectInputValue.length < 2 ? 'Typ om te zoeken...' : 'Geen projecten gevonden'}
        sx={{ mb: 2.5 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Project"
            placeholder="Zoek op nummer, naam of klant..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {projectLoading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Project info chips (auto-filled from project data) */}
      {(context.videoType || context.klant) && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          {context.videoType && <Chip label={context.videoType} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }} />}
          {context.klant && <Chip label={context.klant} size="small" variant="outlined" />}
        </Box>
      )}

      {/* Presets */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Kies een check-profiel
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {PRESET_ORDER.map((pk) => {
          const p = PRESETS[pk];
          const isSelected = selectedPreset === pk;
          return (
            <Paper
              key={pk}
              variant="outlined"
              onClick={() => handlePresetSelect(pk)}
              sx={{
                px: 2, py: 1.5, cursor: 'pointer', borderRadius: 2,
                flex: '1 1 160px', minWidth: 140, maxWidth: 220,
                borderColor: isSelected ? '#6366f1' : '#e5e7eb',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? '#f5f3ff' : '#fff',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#6366f1', bgcolor: '#faf5ff' },
              }}
            >
              <Typography sx={{ fontSize: '1.3rem', mb: 0.5 }}>{p.icon}</Typography>
              <Typography variant="body2" fontWeight={600} noWrap>{p.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
                {p.description}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {selectedPreset === 'custom' && (
        <Chip label="Aangepast profiel" size="small" sx={{ mb: 1, bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }} />
      )}

      {/* Advanced: Check toggles */}
      <Box
        onClick={() => setAdvancedOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
          py: 1, '&:hover': { color: '#6366f1' }, transition: 'color 0.15s',
        }}
      >
        <TuneIcon sx={{ fontSize: 18, opacity: 0.6 }} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Geavanceerd
        </Typography>
        {advancedOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </Box>
      <Collapse in={advancedOpen}>
        <Box sx={{ pl: 1, pb: 1 }}>
          {Object.entries(CATEGORY_META).map(([catKey, meta]) => (
            <FormControlLabel
              key={catKey}
              control={
                <Switch
                  size="small"
                  checked={!!checks[catKey]}
                  onChange={() => handleCheckToggle(catKey)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: meta.color } }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">{meta.icon} {meta.label}</Typography>
                </Box>
              }
              sx={{ display: 'flex', mb: 0.5 }}
            />
          ))}

          {/* Strictness */}
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mt: 1.5, mb: 0.5, display: 'block' }}>
            Gevoeligheid
          </Typography>
          <RadioGroup row value={strictness} onChange={(e) => setStrictness(e.target.value)}>
            {STRICTNESS_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#6366f1' } }} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                  </Box>
                }
                sx={{ mr: 3 }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Collapse>

      {/* Context section */}
      <Box
        onClick={() => setContextOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
          py: 1, '&:hover': { color: '#6366f1' }, transition: 'color 0.15s',
        }}
      >
        <Typography sx={{ fontSize: 16, opacity: 0.6 }}>&#128203;</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Context
        </Typography>
        {contextOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </Box>
      <Collapse in={contextOpen}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, pl: 1, pb: 1.5, pt: 0.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Projecttype</InputLabel>
            <Select
              value={context.videoType || ''}
              label="Projecttype"
              onChange={(e) => handleContextChange('videoType', e.target.value)}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {PROJECT_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Platform</InputLabel>
            <Select
              value={context.platform || ''}
              label="Platform"
              onChange={(e) => handleContextChange('platform', e.target.value)}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {PLATFORM_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Tone of voice</InputLabel>
            <Select
              value={context.tone || ''}
              label="Tone of voice"
              onChange={(e) => handleContextChange('tone', e.target.value)}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {TONE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            size="small" fullWidth label="Doelgroep"
            value={context.doelgroep || ''}
            onChange={(e) => handleContextChange('doelgroep', e.target.value)}
          />

          <TextField
            size="small" fullWidth label="Doel van de video"
            value={context.doel || ''}
            onChange={(e) => handleContextChange('doel', e.target.value)}
          />

          <TextField
            size="small" fullWidth label="CTA"
            value={context.cta || ''}
            onChange={(e) => handleContextChange('cta', e.target.value)}
          />

          <TextField
            size="small" fullWidth label="Briefing / notities"
            value={context.briefing || ''}
            onChange={(e) => handleContextChange('briefing', e.target.value)}
            multiline minRows={2}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />

          {/* Waar moet hij op letten — gaat mee in de beoordeling én in de
              tekst-in-beeld-check (Joris, 11 sep). */}
          <TextField
            size="small" fullWidth label="Waar moet ik extra op letten?"
            placeholder="Bijvoorbeeld: typefout in de animatie rond 0:20, of let op de naam van de klant"
            value={context.focus || ''}
            onChange={(e) => handleContextChange('focus', e.target.value)}
            multiline minRows={2}
            helperText="Eén of twee zinnen is genoeg."
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>
      </Collapse>

      {/* Start button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={handleStart}
          sx={{
            textTransform: 'none', bgcolor: '#6366f1', fontWeight: 600,
            '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, px: 4,
          }}
        >
          Start Analyse
        </Button>
      </Box>
    </Paper>
  );
}
